import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { api, apiError, SITE_ORIGIN } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { UploadSimpleIcon, XCircleIcon } from "@phosphor-icons/react";
import FeePreview from "@/components/FeePreview";

export default function CreateListing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // When this page is opened from a live stream's "+ Add new item" button,
  // the URL carries `?return=/stream/{id}&stream={id}`. We honor `return` so
  // the seller is dropped right back into their live show post-creation, and
  // we auto-attach the new listing to the stream when `stream` is present.
  const returnTo = searchParams.get("return");
  const attachToStreamId = searchParams.get("stream");
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Digital-product state. When `isDigital` is true the seller must
  // upload exactly one file (PDF / ZIP / image / audio / video, max
  // 500 MB) via POST /api/digital/upload. The returned path + filename
  // is attached to the listing on create.
  const [isDigital, setIsDigital] = useState(false);
  const [digitalAsset, setDigitalAsset] = useState(null); // { path, filename, size }
  const [digitalUploading, setDigitalUploading] = useState(false);
  // Carrier registry (canonical list from backend) and the seller's
  // profile defaults — used to label the "Use my defaults" radio and to
  // populate the carrier-override chip row.
  const [allCarriers, setAllCarriers] = useState([]);
  const [profileCarriers, setProfileCarriers] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    price: "",
    type: "fixed",
    starting_bid: "",
    quantity: 1,
    auction_duration_seconds: 30,
    // New shipping model: pick a mode, then (optionally) a fee or carrier
    // override. `default` = inherit the seller's profile defaults entirely.
    shipping_mode: "default", // "default" | "free" | "flat"
    shipping_fee_dollars: "",
    shipping_carriers: [], // empty = inherit seller-profile carriers
  });

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!user.account_type?.endsWith("_seller")) {
      toast.error("Only sellers can create listings.");
      navigate("/dashboard");
      return;
    }
    if (user.verification_status !== "approved") {
      toast.info("Please complete ID verification first.");
      navigate("/verify");
      return;
    }
    api.get("/categories").then((r) => setCategories(r.data.categories));
    // Load carriers + seller's shipping profile so we can render an
    // informed "Use my defaults" hint and pre-select profile carriers.
    api
      .get("/shipping/carriers")
      .then((r) => setAllCarriers(r.data?.items || []))
      .catch(() => {});
    api
      .get("/me/shipping-profile")
      .then((r) => setProfileCarriers(r.data?.carriers || []))
      .catch(() => {});
  }, [user, navigate]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploads = await Promise.all(
        files.map(async (f) => {
          const fd = new FormData();
          fd.append("file", f);
          fd.append("purpose", "listing_image");
          const { data } = await api.post("/files/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          return data.path;
        })
      );
      setImages((prev) => [...prev, ...uploads]);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (p) => setImages((prev) => prev.filter((x) => x !== p));

  const handleDigitalUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024 * 1024) {
      toast.error("File too large (max 500 MB)");
      return;
    }
    setDigitalUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("filename", file.name);
      const { data } = await api.post("/digital/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDigitalAsset({ path: data.path, filename: data.filename, size: data.size });
      toast.success("Digital file ready");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setDigitalUploading(false);
    }
  };

  const removeDigital = () => setDigitalAsset(null);

  const submit = async (e) => {
    e.preventDefault();
    if (isDigital && !digitalAsset) {
      toast.error("Upload the digital file before creating the listing");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        title: form.title,
        description: form.description,
        // Digital listings don't surface a category picker; the backend
        // auto-stamps them as "Digital" so we just send a placeholder
        // string that satisfies the Pydantic model.
        category: isDigital ? "Digital" : form.category,
        price: parseFloat(form.price),
        type: form.type,
        quantity: parseInt(form.quantity || 1, 10) || 1,
        images,
      };
      if (isDigital && digitalAsset) {
        body.is_digital = true;
        body.digital_asset_path = digitalAsset.path;
        body.digital_filename = digitalAsset.filename;
      }
      // Attach to the active live stream if we were opened from one — the
      // backend's POST /api/listings natively accepts `stream_id` and binds
      // the new listing to that stream so it's instantly available to feature.
      if (attachToStreamId) {
        body.stream_id = attachToStreamId;
      }
      if (form.type === "auction") {
        body.starting_bid = parseFloat(form.starting_bid || form.price);
        body.auction_duration_seconds = parseInt(form.auction_duration_seconds || 30, 10);
      }
      // Legacy `shipping_estimate` is only set explicitly for the flat-fee
      // mode so the live-stream "sold" overlay shows an exact number. The
      // new per-listing shipping override (free vs flat + carriers) is
      // applied as a follow-up PUT after the listing is created.
      if (form.shipping_mode === "flat") {
        const shipNum = parseFloat(form.shipping_fee_dollars);
        if (Number.isFinite(shipNum) && shipNum >= 0) body.shipping_estimate = shipNum;
      } else if (form.shipping_mode === "free") {
        body.shipping_estimate = 0;
      }
      const { data } = await api.post("/listings", body);

      // Per-listing shipping override — only PUT when the seller actually
      // chose something other than "Use my defaults" or customized carriers.
      const customCarriers =
        form.shipping_carriers.length > 0 &&
        // Treat as customized only when the chosen set differs from the
        // seller's profile defaults (avoid an unnecessary write).
        form.shipping_carriers.slice().sort().join(",") !==
          profileCarriers.slice().sort().join(",");
      if (form.shipping_mode !== "default" || customCarriers) {
        const override = {
          free_shipping: form.shipping_mode === "free" ? true : null,
          flat_fee_cents:
            form.shipping_mode === "flat" && form.shipping_fee_dollars !== ""
              ? Math.round(parseFloat(form.shipping_fee_dollars) * 100)
              : null,
          carriers: customCarriers ? form.shipping_carriers : null,
        };
        try {
          await api.put(`/listings/${data.id}/shipping`, override);
        } catch (_) {
          /* non-blocking — listing is already created */
        }
      }

      toast.success(attachToStreamId ? "Added to live!" : "Listing created");
      navigate(returnTo || `/listing/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="create-listing-page">
      <Navbar />
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-10">
        <p className="overline text-zinc-500">NEW LISTING</p>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mt-2">
          Create a listing
        </h1>
        <p className="text-zinc-600 mt-2">
          You're listing as a <b>{user.account_type.replace("_", " ")}</b>. Listing will be visible only to
          <b> {user.segment}</b> buyers.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="create-listing-form">
          <div>
            <Label htmlFor="title" className="overline">Title</Label>
            <Input
              id="title"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              required
              className="mt-1"
              data-testid="listing-title-input"
            />
          </div>
          <div>
            <Label htmlFor="desc" className="overline">Description</Label>
            <Textarea
              id="desc"
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              rows={5}
              required
              className="mt-1"
              data-testid="listing-description-input"
            />
          </div>
          {!isDigital && (
            <div>
              <Label className="overline">Category</Label>
              <Select value={form.category} onValueChange={(v) => update("category", v)}>
                <SelectTrigger className="mt-1" data-testid="listing-category-select">
                  <SelectValue placeholder="Choose a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="overline">Listing type</Label>
            <RadioGroup
              value={form.type}
              onValueChange={(v) => update("type", v)}
              className="mt-2 grid sm:grid-cols-2 gap-3"
            >
              <label
                className={`border p-4 cursor-pointer ${form.type === "fixed" ? "border-[#1D78AB] bg-[#1D78AB]/5" : "border-zinc-200"}`}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="fixed" id="t-fixed" data-testid="listing-type-fixed" />
                  <div>
                    <div className="font-display font-bold">Buy Now</div>
                    <p className="text-xs text-zinc-500">Fixed price purchase</p>
                  </div>
                </div>
              </label>
              <label
                className={`border p-4 cursor-pointer ${form.type === "auction" ? "border-[#1D78AB] bg-[#1D78AB]/5" : "border-zinc-200"}`}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="auction" id="t-auction" data-testid="listing-type-auction" />
                  <div>
                    <div className="font-display font-bold">Live Auction</div>
                    <p className="text-xs text-zinc-500">Real-time bidding</p>
                  </div>
                </div>
              </label>
            </RadioGroup>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="price" className="overline">
                {form.type === "fixed" ? "Price (USD)" : "Buy-now / Reserve price"}
              </Label>
              <Input
                id="price"
                type="number"
                min="0.01"
                step="0.01"
                value={form.price}
                onChange={(e) => update("price", e.target.value)}
                required
                className="mt-1"
                data-testid="listing-price-input"
              />
            </div>
            <div>
              <Label htmlFor="qty" className="overline">Quantity available</Label>
              <Input
                id="qty"
                type="number"
                min="1"
                step="1"
                value={form.quantity}
                onChange={(e) => update("quantity", e.target.value)}
                required
                className="mt-1"
                data-testid="listing-quantity-input"
              />
            </div>
          </div>

          {form.type === "auction" && (
            <div>
              <Label htmlFor="sb" className="overline">Starting bid (USD)</Label>
              <Input
                id="sb"
                type="number"
                min="0.01"
                step="0.01"
                value={form.starting_bid}
                onChange={(e) => update("starting_bid", e.target.value)}
                required
                className="mt-1"
                data-testid="listing-starting-bid-input"
              />
            </div>
          )}

          {/* Live fee preview — shows the seller exactly what they'll net,
              including the Founder 0% perk countdown if active. */}
          <FeePreview amount={form.price} />

          {form.type === "auction" && (
            <div>
              <Label htmlFor="dur" className="overline">Auction duration</Label>
              <Select
                value={String(form.auction_duration_seconds)}
                onValueChange={(v) => update("auction_duration_seconds", parseInt(v, 10))}
              >
                <SelectTrigger className="mt-1" data-testid="listing-duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 seconds</SelectItem>
                  <SelectItem value="5">5 seconds</SelectItem>
                  <SelectItem value="10">10 seconds</SelectItem>
                  <SelectItem value="30">30 seconds</SelectItem>
                  <SelectItem value="60">1 minute</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* SHIPPING SECTION — pick a mode then optionally override
              carriers. The "Use my defaults" radio inherits everything
              the seller has set in /seller/shipping. */}
          <div className="border border-zinc-200 rounded-xl p-5 bg-zinc-50/40">
            <div className="overline text-zinc-500 mb-2">Shipping</div>
            <RadioGroup
              value={isDigital ? "digital" : form.shipping_mode}
              onValueChange={(v) => {
                if (v === "digital") {
                  setIsDigital(true);
                } else {
                  setIsDigital(false);
                  setDigitalAsset(null);
                  update("shipping_mode", v);
                }
              }}
              className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2"
              data-testid="shipping-mode-radio"
            >
              <label
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
                  !isDigital && form.shipping_mode === "default"
                    ? "border-[#1D78AB] bg-[#1D78AB]/5"
                    : "border-zinc-200 bg-white"
                }`}
                data-testid="shipping-mode-default"
              >
                <RadioGroupItem value="default" id="ship-default" className="mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold">Use my defaults</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    From your{" "}
                    <a href="/seller/shipping" className="underline" target="_blank" rel="noreferrer">
                      shipping settings
                    </a>
                    .
                  </div>
                </div>
              </label>
              <label
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
                  !isDigital && form.shipping_mode === "free"
                    ? "border-emerald-500 bg-emerald-50"
                    : "border-zinc-200 bg-white"
                }`}
                data-testid="shipping-mode-free"
              >
                <RadioGroupItem value="free" id="ship-free" className="mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold text-emerald-700">Free shipping</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    You absorb the cost. Buyers see a green "Free ship" badge on the card.
                  </div>
                </div>
              </label>
              <label
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
                  !isDigital && form.shipping_mode === "flat"
                    ? "border-[#1D78AB] bg-[#1D78AB]/5"
                    : "border-zinc-200 bg-white"
                }`}
                data-testid="shipping-mode-flat"
              >
                <RadioGroupItem value="flat" id="ship-flat" className="mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold">Flat fee for this listing</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    Override your default just for this one.
                  </div>
                </div>
              </label>
              {/* Digital — no shipping. Selecting this auto-flags the
                  listing as digital and reveals the file uploader below
                  the radios. The buyer is never asked for a shipping
                  address and the order auto-fulfills on payment. */}
              <label
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
                  isDigital
                    ? "border-[#1D78AB] bg-[#1D78AB]/10"
                    : "border-zinc-200 bg-white"
                }`}
                data-testid="shipping-mode-digital"
              >
                <RadioGroupItem value="digital" id="ship-digital" className="mt-0.5" />
                <div className="text-sm min-w-0">
                  <div className="font-semibold text-[#1D78AB] flex items-center gap-1.5 flex-wrap">
                    <span>Digital</span>
                    <span className="text-[9px] font-black tracking-wider bg-[#1D78AB] text-white rounded-full px-1.5 py-px whitespace-nowrap leading-none">
                      NO SHIPPING
                    </span>
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    Deliver a downloadable file. The buyer unlocks it instantly when the order is paid.
                  </div>
                </div>
              </label>
            </RadioGroup>

            {form.shipping_mode === "flat" && !isDigital && (
              <div className="mt-4 max-w-xs">
                <Label htmlFor="shipping-fee" className="overline">
                  Flat fee (USD)
                </Label>
                <Input
                  id="shipping-fee"
                  type="number"
                  step="0.01"
                  min="0"
                  max="999.99"
                  placeholder="e.g. 7.95"
                  value={form.shipping_fee_dollars}
                  onChange={(e) => update("shipping_fee_dollars", e.target.value)}
                  className="mt-1"
                  data-testid="listing-shipping-flat-input"
                  required={form.shipping_mode === "flat"}
                />
              </div>
            )}

            {/* Digital file upload — inline under the radios when the
                seller picks the "Digital" shipping mode. Same logic as
                the standalone uploader: PDF/ZIP/JPG/PNG/MP3/MP4, ≤500MB. */}
            {isDigital && (
              <div className="mt-4">
                {!digitalAsset ? (
                  <label
                    className="flex flex-col items-center justify-center border-2 border-dashed border-[#1D78AB]/40 rounded-lg p-6 cursor-pointer hover:border-[#1D78AB] bg-white"
                    data-testid="digital-upload-zone"
                  >
                    <UploadSimpleIcon size={28} className="text-[#1D78AB]" />
                    <p className="text-sm font-bold mt-2 text-[#1D78AB]">
                      {digitalUploading ? "Uploading…" : "Upload the file to deliver"}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                      PDF, ZIP, JPG, PNG, MP3, MP4 · max 500 MB
                    </p>
                    <input
                      type="file"
                      accept=".pdf,.zip,.jpg,.jpeg,.png,.gif,.webp,.mp3,.mp4,.m4a,.wav"
                      className="hidden"
                      onChange={handleDigitalUpload}
                      disabled={digitalUploading}
                      data-testid="digital-file-input"
                    />
                  </label>
                ) : (
                  <div
                    className="flex items-center gap-3 border border-emerald-300 bg-emerald-50 rounded-lg p-3"
                    data-testid="digital-file-attached"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-emerald-800 truncate">
                        {digitalAsset.filename}
                      </p>
                      <p className="text-xs text-emerald-700">
                        {(digitalAsset.size / (1024 * 1024)).toFixed(2)} MB · ready
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={removeDigital}
                      className="text-emerald-800 hover:text-emerald-900"
                      data-testid="digital-remove-btn"
                      aria-label="Remove digital file"
                    >
                      <XCircleIcon size={22} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Carrier override — collapsed by default. Only shown when
                the seller has actually configured carriers in their
                profile (otherwise nothing meaningful to override). */}
            {allCarriers.length > 0 && !isDigital && (
              <details className="mt-4" data-testid="listing-carriers-override-details">
                <summary className="cursor-pointer text-sm font-semibold text-[#1D78AB] hover:underline">
                  Customize carriers for this listing →
                </summary>
                <p className="mt-2 text-xs text-zinc-500">
                  By default we use the carriers from your{" "}
                  <a href="/seller/shipping" className="underline" target="_blank" rel="noreferrer">
                    shipping settings
                  </a>{" "}
                  ({profileCarriers.length
                    ? profileCarriers
                        .map((s) => allCarriers.find((c) => c.slug === s)?.name || s.toUpperCase())
                        .join(", ")
                    : "none yet"}
                  ). Tick below to override just for this listing.
                </p>
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  {allCarriers.map((c) => {
                    const checked = form.shipping_carriers.includes(c.slug);
                    return (
                      <label
                        key={c.slug}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer ${
                          checked ? "border-[#1D78AB] bg-[#1D78AB]/5" : "border-zinc-200 bg-white"
                        }`}
                        data-testid={`listing-carrier-toggle-${c.slug}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            update(
                              "shipping_carriers",
                              checked
                                ? form.shipping_carriers.filter((s) => s !== c.slug)
                                : [...form.shipping_carriers, c.slug]
                            )
                          }
                          className="h-4 w-4 accent-[#1D78AB]"
                        />
                        <div className="text-sm font-semibold">{c.name}</div>
                      </label>
                    );
                  })}
                </div>
              </details>
            )}
          </div>

          <div>
            <Label className="overline">Photos</Label>
            <div className="mt-2 grid grid-cols-4 gap-2">
              {images.map((p) => (
                <div key={p} className="relative aspect-square border border-zinc-200 group">
                  <img src={`${SITE_ORIGIN}/api/files/${p}`} className="h-full w-full object-cover" alt="" />
                  <button
                    type="button"
                    onClick={() => removeImage(p)}
                    className="absolute top-1 right-1 bg-black/70 text-white p-0.5"
                    data-testid={`remove-image-${p}`}
                  >
                    <XCircleIcon size={18} />
                  </button>
                </div>
              ))}
              <label className="aspect-square border-2 border-dashed border-zinc-300 grid place-items-center cursor-pointer hover:border-[#1D78AB]">
                <UploadSimpleIcon size={28} className="text-zinc-500" />
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleUpload}
                  data-testid="listing-image-input"
                />
              </label>
            </div>
            {uploading && <p className="text-xs text-zinc-500 mt-1">Uploading…</p>}
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#1D78AB] hover:bg-[#165d85] text-white h-11"
            data-testid="create-listing-submit-btn"
          >
            {submitting ? "Creating…" : "Create listing"}
          </Button>
        </form>
      </main>
      <Footer />
    </div>
  );
}
